import React from 'react';
import { useIntl } from 'react-intl';
import { StreamingEarningsSummary } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { DeltaPill } from '../../components/primitives/DeltaPill';
import { formatChangePct } from '../streamingFormat';
import { earningsSources, sourceNames } from '../streamingSources';
import { streamingEarningsMessages as messages } from '../streamingEarningsMessages';
import { streamingTotalsMessages } from '../streamingTotalsMessages';
import { MoneyAmount } from './MoneyAmount';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface EarningsAmountsProps {
  earnings: StreamingEarningsSummary;
}

/**
 * Net and gross of each currency (design e1). Each currency has its own line: amounts of two
 * currencies are never added. The change shows only for a reliable period.
 */
const EarningsAmountsComponent: React.FC<EarningsAmountsProps> = ({ earnings }) => {
  const intl = useIntl();
  const source = sourceNames(earningsSources(earnings), 'shortName');
  const amounts = earnings.amounts ?? [];

  return (
    <div className="d-flex flex-column gap-3" data-testid="streaming-earnings-amounts">
      {amounts.map((amount) => {
        const change = earnings.changeReliable ? formatChangePct(intl, amount.changePct) : null;
        return (
          <div key={amount.currency ?? 'none'} className="d-flex flex-column gap-1">
            <div className={styles.money}>
              <MoneyAmount value={amount.net} currency={amount.currency} className={styles.moneyMain} />
              {change !== null && amount.changePct !== null ? (
                <DeltaPill value={amount.changePct} label={intl.formatMessage(messages.changeNet, { change })} />
              ) : null}
            </div>
            <div className={styles.tileRow}>
              <span className={styles.faint}>{intl.formatMessage(messages.gross)}</span>
              <MoneyAmount value={amount.gross} currency={amount.currency} />
            </div>
            <p className={styles.footnote}>
              {amount.currency === null
                ? intl.formatMessage(messages.noCurrencyLine, { source })
                : intl.formatMessage(messages.currencyLine, { source, currency: amount.currency })}
            </p>
          </div>
        );
      })}
      {!earnings.changeReliable ? (
        <p className={styles.footnote}>{intl.formatMessage(streamingTotalsMessages.changeNotReliable)}</p>
      ) : null}
      {earnings.mayGrow ? (
        <p className={styles.footnote}>{intl.formatMessage(streamingTotalsMessages.mayGrow)}</p>
      ) : null}
    </div>
  );
};

export const EarningsAmounts = TypedMemo(EarningsAmountsComponent);
